import { useState, useRef, useCallback, useEffect } from 'react';
import Logger from '../utils/logger';
import apiService from '../services/api';

export interface LogEntry {
  id: string;
  content: string;
  pod: string;
  timestamp: string;
}

export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

export interface StreamParams {
  pod?: string;
  container?: string;
  filter?: string;   // regex RE2 côté backend (escapé serveur-side)
  since?: number;    // secondes de rétrospective initiale
  tail?: number;     // lignes initiales
}

export interface UseLogStreamReturn {
  logs: LogEntry[];
  connectionStatus: ConnectionStatus;
  connect: (params?: StreamParams) => Promise<void>;
  disconnect: () => void;
  clearLogs: () => void;
  isStreaming: boolean;
  errorMessage: string | null;
}

const MAX_LOG_LINES = 1000;
const MAX_RECONNECT_ATTEMPTS = 5;
const INITIAL_RECONNECT_DELAY = 2000; // 2 seconds
const MIN_TIME_BETWEEN_ERRORS = 1000; // Minimum 1s between error handling

export const useLogStream = (deploymentId: string): UseLogStreamReturn => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectAttemptsRef = useRef<number>(0);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isManualDisconnectRef = useRef<boolean>(false);
  const lastErrorTimeRef = useRef<number>(0);
  const isReconnectingRef = useRef<boolean>(false);
  const currentParamsRef = useRef<StreamParams>({});
  const wasConnectedRef = useRef<boolean>(false);
  const hasReceivedLogsRef = useRef<boolean>(false);

  const clearLogs = useCallback(() => {
    setLogs([]);
  }, []);

  const disconnect = useCallback(() => {
    isManualDisconnectRef.current = true;
    isReconnectingRef.current = false;

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    setConnectionStatus('disconnected');
    setErrorMessage(null);
    reconnectAttemptsRef.current = 0;
    wasConnectedRef.current = false;
    hasReceivedLogsRef.current = false;
  }, []);

  const connect = useCallback(async (params: StreamParams = {}) => {
    // Store params for potential reconnection
    currentParamsRef.current = params;

    // Reset flags
    isManualDisconnectRef.current = false;

    // Close existing connection if any
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    // Clear any pending reconnect (unless this IS a reconnection)
    if (!isReconnectingRef.current && reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
      reconnectAttemptsRef.current = 0;
    }

    isReconnectingRef.current = false;

    setConnectionStatus('connecting');

    // Get a one-time SSE ticket from the backend
    let ticket: string;
    try {
      ticket = await apiService.createSseTicket();
    } catch {
      setConnectionStatus('error');
      setErrorMessage('Session expirée. Veuillez vous reconnecter.');
      return;
    }

    const apiUrl = (window as any)._env_?.REACT_APP_API_URL || 'http://localhost:8000';
    const qs = new URLSearchParams({ ticket });
    if (params.pod) qs.set('pod', params.pod);
    if (params.container) qs.set('container', params.container);
    if (params.filter) qs.set('filter', params.filter);
    if (params.since !== undefined) qs.set('since', String(params.since));
    if (params.tail !== undefined) qs.set('tail', String(params.tail));
    const url = `${apiUrl}/api/v1/logs/applications/${deploymentId}/logs/stream?${qs.toString()}`;

    try {
      const eventSource = new EventSource(url);
      eventSourceRef.current = eventSource;

      eventSource.onopen = () => {
        setConnectionStatus('connected');
        setErrorMessage(null);
        reconnectAttemptsRef.current = 0;
        lastErrorTimeRef.current = 0;
        wasConnectedRef.current = true;
      };

      // Handle log events
      eventSource.addEventListener('log', (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data);
          const logEntry: LogEntry = {
            id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            content: data.content || data.message || '',
            pod: data.pod || 'unknown',
            timestamp: data.timestamp || new Date().toISOString(),
          };

          hasReceivedLogsRef.current = true;
          setLogs(prevLogs => {
            const newLogs = [...prevLogs, logEntry];
            if (newLogs.length > MAX_LOG_LINES) {
              return newLogs.slice(-MAX_LOG_LINES);
            }
            return newLogs;
          });
        } catch (parseError) {
          Logger.warn('Failed to parse log event:', parseError);
        }
      });

      // Handle status events
      eventSource.addEventListener('status', (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data);
          if (data.status === 'end_of_stream') {
            // Stream ended normally, don't reconnect
            eventSource.close();
            eventSourceRef.current = null;
            setConnectionStatus('disconnected');
            setErrorMessage('Fin du stream de logs.');
          } else if (data.status === 'connected') {
            setConnectionStatus('connected');
          }
        } catch (parseError) {
          Logger.warn('Failed to parse status event:', parseError);
        }
      });

      // Handle connection errors
      eventSource.onerror = () => {
        // Ignore if manual disconnect
        if (isManualDisconnectRef.current) {
          return;
        }

        // If we were connected, the stream ended normally (no more logs or stream finished)
        // Don't treat this as an error — just show "disconnected" without reconnecting
        if (wasConnectedRef.current) {
          eventSource.close();
          eventSourceRef.current = null;
          wasConnectedRef.current = false;
          setConnectionStatus('disconnected');
          setErrorMessage(
            hasReceivedLogsRef.current
              ? 'Fin du stream. Cliquez sur "Connecter" pour reprendre.'
              : 'Aucun log disponible. Cliquez sur "Connecter" pour réessayer.'
          );
          return;
        }

        // Debounce rapid error events
        const now = Date.now();
        if (now - lastErrorTimeRef.current < MIN_TIME_BETWEEN_ERRORS) {
          return;
        }
        lastErrorTimeRef.current = now;

        // Close the EventSource to prevent its automatic reconnection
        eventSource.close();
        eventSourceRef.current = null;

        // Check if we should attempt reconnection
        if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
          const delay = INITIAL_RECONNECT_DELAY * Math.pow(2, reconnectAttemptsRef.current);
          reconnectAttemptsRef.current += 1;

          setConnectionStatus('error');
          setErrorMessage(`Connexion perdue. Reconnexion dans ${delay / 1000}s... (${reconnectAttemptsRef.current}/${MAX_RECONNECT_ATTEMPTS})`);

          // Schedule reconnection
          reconnectTimeoutRef.current = setTimeout(() => {
            if (!isManualDisconnectRef.current) {
              isReconnectingRef.current = true;
              connect(currentParamsRef.current);
            }
          }, delay);
        } else {
          setConnectionStatus('error');
          setErrorMessage('Impossible de se connecter. Le service peut être indisponible. Réessayez plus tard.');
        }
      };

    } catch (error) {
      Logger.error('Failed to create EventSource:', error);
      setConnectionStatus('error');
      setErrorMessage('Erreur lors de la création de la connexion');
    }
  }, [deploymentId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isManualDisconnectRef.current = true;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, []);

  return {
    logs,
    connectionStatus,
    connect,
    disconnect,
    clearLogs,
    isStreaming: connectionStatus === 'connected',
    errorMessage,
  };
};

export default useLogStream;
